import { Clock, CalendarX2, AlarmClock } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { todayLuanda } from "../../lib/utils";

const DAYS = [["0", "Seg"], ["1", "Ter"], ["2", "Qua"], ["3", "Qui"], ["4", "Sex"], ["5", "Sáb"], ["6", "Dom"]];
const today = todayLuanda;

export const HRScheduleCard = ({ settings, setSettings }) => {
  const set = (k, v) => setSettings({ ...settings, [k]: v });
  // Apagar a hora no input repõe a hora por defeito (um valor vazio bloquearia o Guardar Tudo)
  const setTime = (k, v, fallback) => set(k, v || fallback);
  const days = settings.hr_work_days || [0, 1, 2, 3, 4, 5];
  const toggleDay = (d) => set("hr_work_days", days.includes(d) ? days.filter((x) => x !== d) : [...days, d].sort());
  const active = !!settings.hr_attendance_since;

  return (
    <Card data-testid="hr-schedule-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Clock className="w-5 h-5" /> Horário e Faltas/Atrasos Automáticos</CardTitle>
        <CardDescription>No check-in o sistema regista o atraso face à hora de entrada; cada dia útil sem check-in fica como falta. Os valores descontam-se automaticamente no salário do mês (editáveis antes de processar).</CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="flex items-center justify-between rounded-lg border p-3">
          <div>
            <p className="font-medium text-sm">Registar faltas automaticamente</p>
            <p className="text-xs text-muted-foreground">{active ? `Activo desde ${settings.hr_attendance_since} — dias úteis sem check-in contam como falta` : "Desligado — só os atrasos são registados no check-in; faltas automáticas já registadas deixam de descontar"}</p>
          </div>
          <Switch checked={active} onCheckedChange={(v) => set("hr_attendance_since", v ? today() : null)} data-testid="hr-auto-absences-switch" />
        </div>

        <div className="grid gap-4 sm:grid-cols-3">
          <div className="space-y-1.5"><Label htmlFor="hr-start">Hora de entrada</Label><Input id="hr-start" type="time" value={settings.hr_work_start || "08:00"} onChange={(e) => setTime("hr_work_start", e.target.value, "08:00")} data-testid="hr-work-start-input" /></div>
          <div className="space-y-1.5"><Label htmlFor="hr-end">Hora de saída</Label><Input id="hr-end" type="time" value={settings.hr_work_end || "17:00"} onChange={(e) => setTime("hr_work_end", e.target.value, "17:00")} data-testid="hr-work-end-input" /></div>
          <div className="space-y-1.5"><Label htmlFor="hr-tol">Tolerância (min)</Label><Input id="hr-tol" type="number" min={0} max={120} value={settings.hr_late_tolerance_min ?? 10} onChange={(e) => set("hr_late_tolerance_min", Number(e.target.value))} data-testid="hr-tolerance-input" /></div>
        </div>

        <div className="space-y-1.5">
          <Label>Dias de trabalho</Label>
          <div className="flex flex-wrap gap-1.5">
            {DAYS.map(([d, label]) => (
              <button key={d} type="button" onClick={() => toggleDay(Number(d))} className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${days.includes(Number(d)) ? "bg-primary text-primary-foreground border-primary" : "bg-background text-muted-foreground"}`} data-testid={`hr-day-${d}`}>{label}</button>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">Feriados nacionais fixos (1 Jan, 4 Fev, 8 Mar, 4 Abr, 1 Mai, 17 Set, 2 Nov, 11 Nov, 25 Dez) não contam como falta.</p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="rounded-lg border p-3 space-y-2">
            <p className="text-sm font-medium flex items-center gap-1.5"><CalendarX2 className="w-4 h-4 text-red-500" /> Valor por falta</p>
            <Select value={settings.hr_absence_deduction_mode || "daily"} onValueChange={(v) => set("hr_absence_deduction_mode", v)}>
              <SelectTrigger data-testid="hr-absence-mode-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">1 dia de salário (salário base ÷ 30)</SelectItem>
                <SelectItem value="fixed">Valor fixo em Kz</SelectItem>
              </SelectContent>
            </Select>
            {(settings.hr_absence_deduction_mode || "daily") === "fixed" && (
              <Input type="number" min={0} step="0.01" value={settings.hr_absence_deduction ?? 0} onChange={(e) => set("hr_absence_deduction", Number(e.target.value))} placeholder="Kz por falta" data-testid="hr-absence-value-input" />
            )}
          </div>
          <div className="rounded-lg border p-3 space-y-2">
            <p className="text-sm font-medium flex items-center gap-1.5"><AlarmClock className="w-4 h-4 text-amber-500" /> Valor por atraso</p>
            <Select value={settings.hr_late_deduction_mode || "fixed"} onValueChange={(v) => set("hr_late_deduction_mode", v)}>
              <SelectTrigger data-testid="hr-late-mode-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="fixed">Valor fixo por cada atraso</SelectItem>
                <SelectItem value="per_minute">Valor por minuto de atraso</SelectItem>
                <SelectItem value="none">Não descontar atrasos</SelectItem>
              </SelectContent>
            </Select>
            {(settings.hr_late_deduction_mode || "fixed") !== "none" && (
              <Input type="number" min={0} step="0.01" value={settings.hr_late_deduction ?? 0} onChange={(e) => set("hr_late_deduction", Number(e.target.value))} placeholder={settings.hr_late_deduction_mode === "per_minute" ? "Kz por minuto" : "Kz por atraso"} data-testid="hr-late-value-input" />
            )}
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="hr-holidays">Feriados adicionais (datas separadas por vírgula, AAAA-MM-DD)</Label>
          <Input id="hr-holidays" value={(settings.hr_extra_holidays || []).join(", ")} onChange={(e) => set("hr_extra_holidays", e.target.value.split(",").map((x) => x.trim()).filter((x) => /^\d{4}-\d{2}-\d{2}$/.test(x)))} placeholder="2026-02-17, 2026-04-03" data-testid="hr-holidays-input" />
        </div>
        <p className="text-xs text-muted-foreground">Cada funcionário pode ter horário e valores próprios em RH › Salários › Dados RH. Guarde com «Guardar Tudo».</p>
      </CardContent>
    </Card>
  );
};
